/**
 * monetization/types — modelo del kit de monetización desacoplada (canon).
 *
 * MODELO DE NEGOCIO por defecto del portfolio: el tier `free` es AD-SUPPORTED
 * (rentable con usuarios gratuitos desde el día 1, `adsEnabled: true`) con
 * quotas generosas-pero-limitadas, y los tiers de pago son ad-free con más
 * almacenamiento/quotas. Todo es configurable por env (defaults) + UI de
 * admin (overrides persistidos) sin tocar código.
 *
 * Espejo TypeScript del modelo Rust (`core-rust/src/models/monetization.rs` +
 * `monetization/resolve.rs`): mantener AMBOS en sintonía.
 */

/** Claves de quota canónicas, comunes a todo el portfolio. */
export const QUOTA_KEYS = ["storage_bytes", "items", "collections", "orgs", "attachments"] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** Límites de un tier. `null` = ilimitado (env: -1). */
export type QuotaLimits = Record<QuotaKey, number | null>;

export interface TierDef {
  /** Id estable ("free", "pro", …). */
  id: string;
  /** Nombre mostrable. */
  label: string;
  /** true ⇒ tier financiado con anuncios (free ad-supported). */
  adsEnabled: boolean;
  limits: QuotaLimits;
}

/** Configuración resuelta (defaults de código → env → overrides de la UI). */
export interface MonetizationConfig {
  /** Master switch; false (default) ⇒ la capa entera es inerte. */
  enabled: boolean;
  paymentsEnabled: boolean;
  /** Id del PaymentProvider inyectado ("none" | "stripe" | …). */
  provider: string;
  /** Tier de quien no tiene suscripción — y de los invitados (guest-first). */
  defaultTier: string;
  tiers: TierDef[];
}

/** Un override persistido (fila de `monetization_config`, editada en la UI admin). */
export interface MonetizationOverrideRow {
  key: string;
  value: unknown;
}

/** Uso real medido server-side, aislado por usuario (CC-40). */
export type UsageSnapshot = Record<QuotaKey, number>;

/** Evaluación de una quota para la UI (barra uso vs límite). */
export interface QuotaEvaluation {
  key: QuotaKey;
  used: number;
  /** null = ilimitado. */
  limit: number | null;
  /** 0..1 (0 si ilimitado). */
  ratio: number;
  /** true a partir del 80 % del límite. */
  nearLimit: boolean;
  exceeded: boolean;
}
